import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';

/** Every row the lookup returns names the shop it belongs to. */
export interface ShopScoped {
  id: string;
  company_id: string | null;
  shop_name: string | null;
}

export interface PlateLookupCar extends ShopScoped {
  license_plate: string;
  state: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  status: boolean | null;
  is_family_plan: boolean | null;
  customer_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Stored under another O/0 spelling than the one searched. */
  spelling_differs?: boolean;
}

export interface PlateLookupCustomer extends ShopScoped {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  contact: string | null;
  license_plate: string | null;
  source: string | null;
  source_id: string | null;
  status: boolean | null;
  customer_status: string | null;
  created_at: string | null;
}

export interface PlateLookupMembership extends ShopScoped {
  customer_id: string | null;
  order_id: string | null;
  status: string;
  source: string;
  source_id: string | null;
  vendor_customer_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  cancel_at_period_end?: boolean | null;
  /** `scheduled` or `active` (paused now); null when no pause. */
  pause_status?: string | null;
  created_at: string | null;
  package_name: string | null;
  /** Plate of the car on the membership's order, when it has one. */
  order_vehicle_plate?: string | null;
  /** The shop's time zone, for showing its dates on its own calendar. */
  timezone?: string | null;
  family_link_status?: string | null;
  is_active: boolean;
  period_ended: boolean;
}

export interface PlateLookupGateRead extends ShopScoped {
  plate: string;
  state: string | null;
  event_timestamp: string;
  event: string | null;
  message: string | null;
  is_completed: boolean;
  lane_name: string | null;
  location_name: string | null;
  timezone: string | null;
  confidence: number | null;
  has_image: boolean;
  subscription_code: string | null;
  spelling_differs?: boolean;
}

export interface PlateLookupPlateChange extends ShopScoped {
  action: string;
  previous_plate: string | null;
  previous_state: string | null;
  new_plate: string;
  new_state: string | null;
  location_name: string | null;
  created_at: string;
}

export interface PlateLookupSection<T> {
  rows: T[];
  /** Set when this source could not be read; the rest of the page still loaded. */
  error?: 'unavailable';
}

export type ActivityType =
  | 'lpr_scanned'
  | 'membership_redeemed'
  | 'rewash'
  | 'purchase'
  | 'membership_renewal'
  | 'membership_started'
  | 'membership_cancelled'
  | 'way_plus';

export interface ActivityScan {
  id: string;
  plate: string;
  state: string | null;
  confidence: number | null;
  hasImage: boolean;
}

/** One row of the Activity list. Fields beyond the common ones depend on `type`. */
export interface ActivityEvent {
  id: string;
  type: ActivityType;
  at: string;
  companyId: string | null;
  shopName: string | null;
  locationName: string | null;
  laneName: string | null;
  timezone: string | null;
  code?: string | null;
  planName?: string | null;
  scan?: ActivityScan | null;
  vehiclePlate?: string | null;
  vehicleMismatch?: boolean;
  otherVehicle?: boolean;
  event?: string | null;
  message?: string | null;
  isCompleted?: boolean;
  reason?: string | null;
  kind?: string;
  amount?: number | null;
  paymentStatus?: string | null;
  source?: string | null;
  status?: string | null;
  planChange?: boolean;
}

export interface PurchaseRow {
  id: string;
  at: string;
  kind: string;
  planName: string | null;
  washType: string | null;
  amount: number | null;
  paymentStatus: string | null;
  source: string | null;
  locationName: string | null;
  timezone: string | null;
  vehiclePlate: string | null;
  /** The shop the purchase was made at; null for Way+, which belongs to no shop. */
  companyId: string | null;
}

/** Every alert names the one shop it is about. */
interface AlertShop {
  companyId: string | null;
  shopName: string | null;
}

export type PlateAlert = AlertShop &
  (
    | { type: 'vehicle_mismatch'; count: number; readPlate: string; vehiclePlates: string[] }
    | { type: 'duplicate_customer'; count: number; codes: string[] }
    | {
        type: 'close_washes';
        total: number;
        days: { at: string; timezone: string | null; count: number; minutes: number }[];
      }
    | { type: 'active_period_ended'; count: number; codes: string[] }
  );

export interface PlateLookupShopSummary {
  companyId: string | null;
  shopName: string | null;
  cars: number;
  customers: number;
  memberships: number;
  activeMembership: PlateLookupMembership | null;
  latestEnded: PlateLookupMembership | null;
  lastSeen: PlateLookupGateRead | null;
  lastWash: ActivityEvent | null;
  washesRedeemed: number;
  rewashes: number;
  lastImport: { status: string; startedAt: string; completedAt: string | null } | null;
}

export type DrbSyncReason =
  'active_membership' | 'membership_expired' | 'no_membership' | 'not_in_our_records';

export interface DrbSyncShop {
  companyId: string;
  shopName: string;
  reason: DrbSyncReason;
  locations: { id: string; name: string | null }[];
  customers: { id: string; name: string }[];
}

export interface PlateLookupResult {
  query: { plate: string; state: string | null; candidates: string[] };
  summary: { shops: PlateLookupShopSummary[] };
  alerts: PlateAlert[];
  timeline: PlateLookupSection<ActivityEvent>;
  purchases: PlateLookupSection<PurchaseRow>;
  washStats: Record<string, { washes: number; lastWashAt: string | null }>;
  sections: {
    cars: PlateLookupSection<PlateLookupCar>;
    customers: PlateLookupSection<PlateLookupCustomer>;
    memberships: PlateLookupSection<PlateLookupMembership>;
    gateReads: PlateLookupSection<PlateLookupGateRead>;
    plateChanges: PlateLookupSection<PlateLookupPlateChange>;
    lookAlikes: PlateLookupSection<PlateLookupCar>;
  };
  drbSync: {
    shops: DrbSyncShop[];
    error?: 'unavailable';
    hiddenReason?: 'active_way_subscription' | 'way_only' | 'no_drb_location';
  };
}

/** Super-admin Plate Lookup (CWS-1703). */
@Injectable({ providedIn: 'root' })
export class PlateLookupService {
  private readonly api = inject(ApiService);

  lookup(plate: string, state?: string | null): Observable<PlateLookupResult> {
    const params: Record<string, string> = { plate };
    if (state) {
      params['state'] = state;
    }
    return this.api
      .getWithSuperAdminToken<PlateLookupResult>('admin/super-admin/plate-lookup', params)
      .pipe(map(res => res.data as PlateLookupResult));
  }
}
